import { useSetState } from '@/shared/lib/customHooks';
import { initialMedicalState } from '../../lib/patientDetailsTab';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import type { MedicalDrawerProps } from './MedicalDrawer.types';

/**
 * Holds the editable medical-history form of the study's patient details drawer. Seeds merged state
 * from the study's onboarding data and wires a field change handler plus the submit that passes the
 * symptoms payload to handleClick.
 *
 * @param props - Study data and the parent save callback handleClick.
 * @returns An object carrying the form state, the field change handler and the submit handler.
 */
export const useMedicalDrawer = ({
  data,
  handleClick,
}: Pick<MedicalDrawerProps, 'data' | 'handleClick'>) => {
  const [state, setState] = useSetState<any>(initialMedicalState(data));

  /**
   * Records an edit to one medical-history drawer field. Writes the value into merged state under the field name.
   *
   * @param name - Field being edited.
   * @param value - New field value.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Saves the patient's medical history entered in the drawer. Sends the selected patient symptoms and, only when symptoms were entered, the symptom frequency as a single-item list and the used-heart-monitor answer, to the parent save callback under the medical-history key with a completed status.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const hasEnteredPatientSymptoms = state.patientSymptoms?.length > 0;
    const payload = {
      onboarding: {
        patientSymptoms: state.patientSymptoms?.length > 0 ? state.patientSymptoms : [],
        frequentlyOfSymptoms:
          hasEnteredPatientSymptoms && state.frequentlyOfSymptoms
            ? [state.frequentlyOfSymptoms]
            : [],
        usedHeartMonitor: hasEnteredPatientSymptoms ? state?.usedHeartMonitor : null,
      },
    };
    handleClick({
      payload,
      status: true,
      key: PATIENT_DETAIL_KEY.MEDICAL_HISTORY,
    });
  };

  return { state, onChange, onClickBottomButton };
};
